import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { WebinarContainer } from '@generated/extended';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18nModule } from '@app/i18n';
import { Router } from '@angular/router';

interface News {
  id: number;
  url: string;
  language: string;
}
@UntilDestroy()
@Component({
  selector: 'app-webinar',
  standalone: true,
  imports: [TranslocoModule, CommonModule, I18nModule],
  templateUrl: './webinar.component.html',
  styleUrls: ['./webinar.component.scss'],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['products'],
    },
  ],
})
export class WebinarComponent implements OnInit {
  private _translocoService = inject(TranslocoService);
  private router = inject(Router);

  private langKey = signal<string>('de');

  private webinars: WebinarContainer[] = [
    // building-costs https://digi-neubaukosten.events.sia.ch/
    {
      url: 'https://digi-neubaukosten.events.sia.ch/',
      type: 'BUILDING_COSTS_AND_DATES',
      date: '09.09.2025',
      alias: 'building-costs',
      language: 'de',
    },
    {
      url: 'https://digi-neubaukosten.events.sia.ch/',
      type: 'BUILDING_COSTS_AND_DATES',
      date: '07.10.2025',
      alias: 'building-costs',
      language: 'de',
    },
    {
      url: 'https://digi-neubaukosten.events.sia.ch/',
      type: 'BUILDING_COSTS_AND_DATES',
      date: '04.11.2025',
      alias: 'building-costs',
      language: 'de',
    },
    {
      url: 'https://digi-neubaukosten.events.sia.ch/',
      type: 'BUILDING_COSTS_AND_DATES',
      date: '02.12.2025',
      alias: 'building-costs',
      language: 'de',
    },
    // rebuild-costs https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/
    {
      url: 'https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/',
      type: 'REBUILD_COSTS',
      date: '23.09.2025',
      alias: 'rebuild-costs',
      language: 'de',
    },
    {
      url: 'https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/',
      type: 'REBUILD_COSTS',
      date: '21.10.2025',
      alias: 'rebuild-costs',
      language: 'de',
    },
    {
      url: 'https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/',
      type: 'REBUILD_COSTS',
      date: '25.11.2025',
      alias: 'rebuild-costs',
      language: 'de',
    },
  ];

  private news: News[] = [
    {
      id: 1,
      url: `pdf/${this.langKey}/keeValue_LCA.pdf`,
      language: 'de',
    },
    {
      id: 2,
      url: 'https://www.swissbau.ch/de/c/lebenszykluskosten-praezision-durch-kuenstliche-intelligenz.43791',
      language: 'de',
    },
    {
      id: 3,
      url: 'https://digitalisierte-umbaukostenschaetzungen.events.sia.ch/',
      language: 'de',
    },
  ];

  ngOnInit(): void {
    this.updateLanguage();
  }

  getNextWebinars = computed(() => {
    const today = new Date();
    return this.webinars
      .filter((webinar) => {
        const [day, month, year] = webinar.date.split('.');
        const webinarDate = new Date(`${year}-${month}-${day}`);
        return webinar.language === this.langKey() && webinarDate >= today; // Keep only upcoming webinars
      })
      .sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('.');
        const [dayB, monthB, yearB] = b.date.split('.');
        const dateA = new Date(`${yearA}-${monthA}-${dayA}`);
        const dateB = new Date(`${yearB}-${monthB}-${dayB}`);
        return dateA.getTime() - dateB.getTime(); // Sort by ascending date
      })
      .slice(0, 3); // Limit to the next 3 webinars
  });

  getNews = computed(() => {
    return this.news.filter((news) => news.language === this.langKey());
  });

  pdfLanguagePath = computed(() => {
    return this.langKey().toLowerCase();
  });

  openCO2Pdf(event: Event) {
    event.preventDefault();
    const pdfPath = `pdf/${this.pdfLanguagePath()}/keeValue_CO2.pdf`;

    fetch(pdfPath, { method: 'HEAD' })
      .then((response) => {
        if (response.ok) {
          window.open(pdfPath, '_blank');
        } else {
          this.router.navigate(['/404']);
        }
      })
      .catch(() => {
        this.router.navigate(['/404']);
      });
  }

  updateLanguage() {
    this._translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (lang) => {
        const [langKey] = lang.split('-');
        this.langKey.set(langKey);
      },
    });
  }

  isDateInPast(dateString: string): boolean {
    const [day, month, year] = dateString.split('.');
    const webinarDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    return webinarDate < today;
  }
}
